import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { Component } from 'react';
import { RouteComponentProps, StaticContext, withRouter } from 'react-router';
import CheckoutPreloadingSkeletons from './components/CheckoutPreloadingSkeletons/CheckoutPreloadingSkeletons';
import OrderEditHeader from './components/OrderEditHeader/OrderEditHeader';
import CheckoutProductsTab from './containers/CheckoutProductsTab/CheckoutProductsTab';
import CheckoutSummaryTab from './containers/CheckoutSummaryTab/CheckoutSummaryTab';
import CustomerDetailsTab from './containers/CustomerDetailsTab/CustomerDetailsTab';
import OrderCreateSidebar from './containers/OrderCreateSidebar/OrderCreateSidebar';
import { withCheckoutStore } from './context';
import { discardCb } from './helpers/checkout';
import { ORDER_EDIT_STAGE } from 'src/mobx/checkoutStore/constants';
import { TEventMessage, TOrder, TOrderCRUDEvents, orderCast } from 'src/types/orders';
import { leaveWithoutSaving } from 'src/utils/modalConfirmations';
import TabList from 'src/view/components/Tabs/TabList';
import Tabs from 'src/view/components/Tabs/Tabs';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { ORDER_STATUS } from '~/constants/order';
import { USER_ROLES } from '~/constants/user';
import { CheckoutStore } from '~/mobx/checkoutStore';
import { localeStore } from '~/mobx/localesStore';
import { modalStore } from '~/mobx/modalStore';
import { CallCenterStore, callCenterStore } from '~/mobx/ordersStore/callCenterStore';
import { OrderTabs } from '~/mobx/ordersStore/types';
import { userStore } from '~/mobx/userStore';
import { history } from '~/services/history';
import { CustomEvents, subscribe, unsubscribe } from '~/utils/events';
import getOrderViewPath from '~/view/screens/CheckoutScreen/getOrderViewPath';
import getOrdersPath from '~/view/screens/CheckoutScreen/getOrdersPath';
import './style.scss';

type TProps = RouteComponentProps<{ id: string }, StaticContext, TOrder | undefined> & { checkoutStore?: CheckoutStore };

const callCenterStatuses = [ORDER_STATUS.DRAFT, ORDER_STATUS.ON_HOLD, ORDER_STATUS.WAITING_FOR_PAYMENT];
const notEditableStatuses = [ORDER_STATUS.CANCELED, ORDER_STATUS.WAITING_FOR_PAYMENT, ORDER_STATUS.ON_HOLD];

// @ts-ignore
@withCheckoutStore
@observer
class CheckoutScreen extends Component<TProps, {}> {
  get checkoutStore() {
    return this.props.checkoutStore!;
  }

  componentDidMount() {
    this.props.history.block((location, action) => {
      if (action && action === 'POP') {
        leaveWithoutSaving({ onConfirm: () => discardCb(this.checkoutStore) });
        return false;
      }
      return undefined;
    });

    this.decompose();
    subscribe(CustomEvents.SocketUpdate, this.handleSocketEvent);
    subscribe(CustomEvents.SocketReconnect, this.handleSocketReconnect);
  }

  handleSocketReconnect = () => {
    // TOFIX
    return;
  };

  handleSocketEvent = ({ detail }: { detail: TEventMessage<TOrder, TOrderCRUDEvents> }) => {
    const { data } = detail;
    const order = orderCast(data);

    if (this.checkoutStore.dependencies.order?.id === order.id) {
      this.checkoutStore.dependencies.order = order;
    }
  };

  componentDidUpdate = (prevProps: TProps) => {
    if (this.props.match.params.id !== prevProps.match.params.id) {
      this.decompose();
    }

    // Redirect RM if order status for CallCenter only
    if (
      this.checkoutStore.dependencies.order &&
      !(this.checkoutStore.parentStore instanceof CallCenterStore) &&
      callCenterStatuses.includes(this.checkoutStore.dependencies.order.order_status_code)
    ) {
      if (userStore.user.role === USER_ROLES.RESTAURANT_MANAGER) {
        history.push(getOrderViewPath(callCenterStore, this.checkoutStore.dependencies.order.id), toJS(this.checkoutStore.dependencies.order));
      } else {
        // Redirect other roles to OrdersList if order status for CallCenter only
        modalStore.openErrorModal({ message: localeStore.t('possible_you_dont_have_permissions_or_page_not_found') });
        history.push(getOrdersPath(this.checkoutStore.parentStore));
      }
      // Redirect if order statuses is available for View only
    } else if (this.checkoutStore.dependencies.order && notEditableStatuses.includes(this.checkoutStore.dependencies.order.order_status_code)) {
      if (this.checkoutStore.parentStore instanceof CallCenterStore && this.checkoutStore.isDraft) {
        this.checkoutStore.parentStore.setActiveTab(OrderTabs.All);
      }
      history.push(getOrderViewPath(this.checkoutStore.parentStore, this.checkoutStore.dependencies.order.id));
    }
  };

  decompose = () => {
    this.checkoutStore.clear();

    this.checkoutStore.decompose(this.props.match.params.id);
  };

  componentWillUnmount = () => {
    if (this.checkoutStore.parentStore instanceof CallCenterStore) {
      this.checkoutStore.parentStore.showOrderList();
    }
    this.checkoutStore.clear();
    unsubscribe(CustomEvents.SocketUpdate, this.handleSocketEvent);
    unsubscribe(CustomEvents.SocketReconnect, this.handleSocketReconnect);
  };

  get orderEditStagesTabs() {
    const tabs = [
      { label: 'items', value: ORDER_EDIT_STAGE.PRODUCTS },
      { label: 'summary_and_checkout', value: ORDER_EDIT_STAGE.CHECKOUT },
    ];
    if (this.checkoutStore.isDraft) {
      tabs.unshift({ label: 'Customer Details', value: ORDER_EDIT_STAGE.DETAILS });
    }

    return tabs;
  }

  get scrollBlockClassName(): string {
    let className: string = 'checkout-tabs';

    className += this.checkoutStore.orderEditStage === ORDER_EDIT_STAGE.DETAILS ? ' px-4' : ' pr-4';
    if (this.checkoutStore.parentStore instanceof CallCenterStore) className += ' call-center';

    return className;
  }

  render() {
    if (!this.checkoutStore.dependencies.order) {
      return <CheckoutPreloadingSkeletons />;
    }

    return (
      <div className="d-flex flex-1 flex-column px-4">
        <OrderEditHeader />
        <div className="d-flex flex-1 pt-6">
          <ScrollableBlock showScrollbar scrollbarSize="small" className="flex-1 mr-2" scrollClassName={this.scrollBlockClassName}>
            <Tabs
              disabled={this.checkoutStore.validatorErrors.branch || this.checkoutStore.validatorErrors.delivery_type}
              errors={this.checkoutStore.validatorErrors}
              onChange={this.checkoutStore.setOrderEditStage}
              options={this.orderEditStagesTabs}
              value={this.checkoutStore.orderEditStage}
            />

            <TabList
              tabs={[ORDER_EDIT_STAGE.DETAILS, ORDER_EDIT_STAGE.PRODUCTS, ORDER_EDIT_STAGE.CHECKOUT]}
              value={this.checkoutStore.orderEditStage}
            >
              <CustomerDetailsTab />
              <CheckoutProductsTab />
              <CheckoutSummaryTab />
            </TabList>
          </ScrollableBlock>
          <OrderCreateSidebar checkoutStore={this.checkoutStore} />
        </div>
      </div>
    );
  }
}

export default withRouter(CheckoutScreen);
